import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";

import type { User } from "../api/types";
import { useLogout } from "../hooks/useAuth";
import ThemeToggle from "./ThemeToggle";

type NavItem = { to: string; label: string; end?: boolean };

const NAV: NavItem[] = [
  { to: "/", label: "Applications", end: true },
  { to: "/board", label: "Board" },
  { to: "/profile", label: "Profile" },
  { to: "/templates", label: "Templates" },
];

// Hiding this is a convenience; the server enforces the same check.
const ADMIN_NAV: NavItem[] = [{ to: "/invites", label: "Invites" }];

export default function Layout({ user, children }: { user: User; children: ReactNode }) {
  const logout = useLogout();
  // The board's seven columns need more room than a reading-width page.
  const wide = useLocation().pathname === "/board";

  return (
    <div className="min-h-dvh bg-gray-50">
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-surface">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          <span className="text-base font-semibold text-accent-700">JobSearcher</span>

          <nav className="flex flex-wrap gap-1">
            {[...NAV, ...(user.is_admin ? ADMIN_NAV : [])].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  "rounded-md px-3 py-1.5 text-sm font-medium transition-colors " +
                  (isActive
                    ? "bg-accent-50 text-accent-700"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ms-auto flex items-center gap-3">
            <ThemeToggle />
            <NavLink
              to="/account"
              title="Account settings"
              className={({ isActive }) =>
                "max-w-48 truncate rounded-md px-2 py-1 text-sm transition-colors " +
                (isActive
                  ? "bg-accent-50 text-accent-700"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900")
              }
            >
              {user.display_name}
            </NavLink>
            <button
              type="button"
              onClick={() => logout.mutate()}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className={"mx-auto py-8 " + (wide ? "px-6" : "max-w-5xl px-4")}>
        {children}
      </main>
    </div>
  );
}
